import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import CheckManagementForm from '../../components/agent/CheckManagementForm';

// ============================================
// Component: AgentNewPaymentPage
// Purpose: Full-page payment collection form optimized for tablet/iPad
// UX Notes:
// - Full page instead of modal for better tablet scrolling
// - Touch-friendly large inputs
// - Clear visual hierarchy
// ============================================

const AgentNewPaymentPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    check_number: '',
    bank_number: '',
    branch_number: '',
    bank_reference: '',
    notes: '',
    checks: []
  });

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/field-agent/payments/company/${companyId}`);
      setCompany({
        id: companyId,
        name: response.data.company?.name,
        current_balance: response.data.credit_summary?.current_balance || 0,
        available_credit: response.data.credit_summary?.available_credit || 0,
        opening_balance: response.data.credit_summary?.opening_balance || 0,
        credit_limit: response.data.credit_summary?.credit_limit || 0
      });
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast.error('שגיאה בטעינת נתוני החברה');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    
    if (!formData.amount || amount <= 0) {
      toast.error('נא להזין סכום תקין');
      return;
    }

    // Validate checks if payment method is check
    if (formData.payment_method === 'check') {
      if (!formData.checks || formData.checks.length === 0) {
        toast.error('נא להוסיף לפחות צ\'ק אחד');
        return;
      }
      const checksTotal = formData.checks.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
      if (Math.abs(checksTotal - amount) > 0.01) {
        toast.error(`סכום הצ'קים (${formatCurrency(checksTotal)}) לא תואם לסכום הגבייה (${formatCurrency(amount)})`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await axiosInstance.post('/api/field-agent/payments', {
        company_id: companyId,
        ...formData,
        amount: amount
      });

      toast.success('התשלום נרשם בהצלחה!');
      navigate(`/agent/payments/${companyId}`);
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.message || 'שגיאה ברישום התשלום');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/agent/payments/${companyId}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-lg"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              ביטול
            </button>
            <h1 className="text-xl font-bold text-gray-900">רישום גבייה חדשה</h1>
            <div className="w-16"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Company Info Card */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 mb-6 text-white shadow-lg">
          <div className="text-sm opacity-90 mb-1">חברה</div>
          <div className="text-2xl font-bold mb-4">{company?.name}</div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-xs opacity-90">חוב נוכחי</div>
              <div className="text-xl font-bold">
                {formatCurrency(company?.current_balance)}
              </div>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <div className="text-xs opacity-90">מסגרת אשראי</div>
              <div className="text-xl font-bold">
                {formatCurrency(company?.credit_limit)}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              סכום גבייה
            </label>
            <div className="relative">
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl text-gray-500">₪</span>
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="w-full pr-12 pl-4 py-4 text-3xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
                required
              />
            </div>
            {company?.current_balance > 0 && (
              <button
                type="button"
                onClick={() => setFormData({...formData, amount: company.current_balance.toFixed(2)})}
                className="mt-3 text-primary-600 hover:text-primary-800 text-sm font-medium"
              >
                גבה את כל החוב ({formatCurrency(company.current_balance)})
              </button>
            )}
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              אמצעי תשלום
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'cash', label: 'מזומן', icon: '💵' },
                { value: 'check', label: 'צ\'קים', icon: '📝' },
                { value: 'bank_transfer', label: 'העברה', icon: '🏦' }
              ].map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setFormData({...formData, payment_method: method.value, checks: []})}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.payment_method === method.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{method.icon}</div>
                  <div className="text-sm font-medium">{method.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Check Details */}
          {formData.payment_method === 'check' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                פרטי צ'קים
              </label>
              <CheckManagementForm
                formData={{...formData, amount: formData.amount || '0'}}
                setFormData={setFormData}
              />
            </div>
          )}

          {/* Bank Transfer Details */}
          {formData.payment_method === 'bank_transfer' && (
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                פרטי העברה בנקאית
              </label>
              <input
                type="text"
                value={formData.bank_reference}
                onChange={(e) => setFormData({...formData, bank_reference: e.target.value})}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="מספר אסמכתא"
              />
            </div>
          )}

          {/* Date */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              תאריך תשלום
            </label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <label className="block text-lg font-semibold text-gray-900 mb-3">
              הערות (אופציונלי)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="הערות נוספות..."
            />
          </div>

          {/* Submit Button */}
          <div className="pb-8">
            <button
              type="submit"
              disabled={submitting || !formData.amount}
              className="w-full py-5 bg-green-600 text-white text-xl font-bold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
            >
              {submitting ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  שומר...
                </div>
              ) : (
                `רשום גבייה - ${formatCurrency(parseFloat(formData.amount) || 0)}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentNewPaymentPage;

